import {
    doppeActionTypeCreateSettingsResolvingFunction,
    doppeActionTypeDefineMetadata
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {RichTextContent, Values} from '@wix/devzai-utils-common';
import {LocationCoordinates} from '@wix/devzai-utils-common';
import {
    DoppeHideableValue,
    doppeHideableValueCreateHidden
} from '../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingsMetadata,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';

/**
 * Height options for the Location Map widget.
 */
export const LocationMapWidgetHeight = {
    Compact: 'Compact',
    Normal: 'Normal',
    Large: 'Large',
} as const;

export type LocationMapWidgetHeight = Values<typeof LocationMapWidgetHeight>;

/**
 * Pixel height values for each height option.
 */
const LocationMapWidgetHeightValues = {
    Compact: 200,
    Normal: 300,
    Large: 450,
} as const;

/**
 * Returns the pixel height value for a given height option.
 */
export function locationMapWidgetHeightGetPixelValue(height: LocationMapWidgetHeight): number {
    return LocationMapWidgetHeightValues[height];
}

/**
 * Theme options for the Location Map widget.
 * - Dynamic: Use page/card scope colors to style the map (brand-matched, recommended)
 * - Regular: Always use light/standard map colors
 * - Dark: Always use dark map colors (inverted, for dark backgrounds)
 */
export const LocationMapTheme = {
    Dynamic: 'Dynamic',
    Regular: 'Regular',
    Dark: 'Dark',
} as const;

export type LocationMapTheme = Values<typeof LocationMapTheme>;

/**
 * Base settings for the location map action, including location and display configuration.
 */
export interface DoppeActionTypeLocationMapBaseActionSettings {
    /**
     * Title of the location map action (visible to users).
     */
    title: DoppeHideableValue<string>;

    /**
     * Description text displayed below the map.
     */
    description: DoppeHideableValue<RichTextContent>;

    /**
     * Location information for the map.
     */
    location: {
        /**
         * Geographical coordinates (latitude/longitude) for the map center.
         */
        locationCoordinates: LocationCoordinates | null;

        /**
         * Human-readable address text displayed to users.
         */
        displayAddress: DoppeHideableValue<string>;

        /**
         * Whether to show the display address section in the widget.
         * When false, only the map is shown (address/navigation handled at action level).
         * Default: true for existing actions (preserve behavior), false for new actions.
         */
        showDisplayAddress: boolean;

        /**
         * Suggested title from the map API (nameSuggestions[0]).
         * Used to pre-fill the action title during factory creation.
         */
        suggestedTitle: string | null;
    };

    /**
     * Map zoom level (1-21 scale, where 21 is most zoomed in).
     * Default: 14
     */
    zoom: number;

    /**
     * Height of the map widget (Compact, Normal, or Large).
     * Default: Normal
     */
    height: LocationMapWidgetHeight;

    /**
     * Theme of the map widget (Dynamic, Regular, or Dark).
     * Dynamic: Uses page/card colors to style the map (recommended)
     * Regular: Always light/standard map
     * Dark: Always dark/inverted map colors
     * Default: Dynamic
     */
    mapTheme: LocationMapTheme;
}

/**
 * Complete action settings including base map settings, content sections, and CTA buttons.
 */
export interface DoppeActionTypeLocationMapActionSettings extends
    DoppeActionTypeLocationMapBaseActionSettings,
    DoppeActionContentSectionsSettings,
    DoppeActionCtaButtonsSettings {
}

/**
 * Namespace for Location Map action type convenience type aliases.
 */
export namespace DoppeActionTypeLocationMap {
    export type Height = LocationMapWidgetHeight;
    export type Theme = LocationMapTheme;
    export type BaseActionSettings = DoppeActionTypeLocationMapBaseActionSettings;
    export type ActionSettings = DoppeActionTypeLocationMapActionSettings;
}

/**
 * Content settings metadata for the location map action.
 * Defines which fields are considered content for AI features, translation, etc.
 */
export const doppeActionTypeLocationMapActionContentSettingsMetadata = {
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    location: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    zoom: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    height: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    mapTheme: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeLocationMapBaseActionSettings>;

/**
 * Location Map action type metadata definition.
 * Defines behavior, defaults, and capabilities of the location map action.
 */
export const doppeActionTypeLocationMap = doppeActionTypeDefineMetadata<DoppeActionTypeLocationMapActionSettings>({
    id: 'io.bymo.action/location-map',
    resolveActionSettings: doppeActionTypeCreateSettingsResolvingFunction({
        title: '',
        description: '',
        location: {
            locationCoordinates: {latitude: 40.7580, longitude: -73.9855},
            displayAddress: '',
            showDisplayAddress: false,
            suggestedTitle: null
        },
        zoom: 14,
        height: LocationMapWidgetHeight.Normal,
        mapTheme: LocationMapTheme.Regular,
        sections: [],
        ctaButtons: [],
    }),

    /**
     * Action settings metadata configuration.
     */
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeLocationMapActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
            description: true,
            title: true
        }
    },

    /**
     * Location Map action doesn't support list items.
     */
    actionListItemSettingsMetadata: false,

    /**
     * Location Map action opens a widget (not a modal or external link as primary action).
     */
    isWidgetOpener: true,

    /**
     * Location Map action doesn't open external links as its primary function.
     */
    isLinkOpener: false,

    /**
     * Location Map action can be rendered directly in a page.
     */
    canRenderInPage: true,

    /**
     * Don't skip reporting when button is clicked.
     */
    skipReportingOnGoalAchievedOnActionButtonClick: false,

    /**
     * Location Map action doesn't use media resources.
     */
    resolveUsedMedia: _actionSettings => [],

    /**
     * Location Map action doesn't have main media.
     */
    resolveMainMedia: _actionSettings => null,

    /**
     * Location Map action doesn't resolve to a main link (it's about showing a map, not navigating).
     */
    resolveMainLink: (_actionSettings, _viewerLang) => null,

    /**
     * Location Map action doesn't use business entities.
     */
    resolveUsedBusinessEntities: _actionSettings => [],

    /**
     * Prepare action settings for viewer (handle locked actions).
     */
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.location = {
                locationCoordinates: null,
                displayAddress: doppeHideableValueCreateHidden(''),
                showDisplayAddress: actionSettings.location.showDisplayAddress,
                suggestedTitle: null
            };
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    }
});
